import { Group, rem, Text, useMantineTheme } from "@mantine/core";
import { Dropzone as MantineDropzone, FileRejection, MIME_TYPES } from "@mantine/dropzone";
import { IconCloudUpload, IconDownload, IconX } from "@tabler/icons-react";
import React, { useRef } from "react";

interface Props {
  onDrop: (file: File[]) => void;
  onReject?: (files: FileRejection[]) => void;
  acceptableMimeTypes: (keyof typeof MIME_TYPES)[];
  title: string;
  maxSize?: number;
  multiple?: boolean;
  maxFiles?: number;
}

const Dropzone: React.FC<Props> = ({
  onDrop,
  onReject,
  acceptableMimeTypes,
  title,
  maxSize,
  multiple,
  maxFiles,
}) => {
  const theme = useMantineTheme();
  const openRef = useRef<() => void>(null);

  const mimeTypes = acceptableMimeTypes.map((type) => MIME_TYPES[type]);

  return (
    <MantineDropzone
      openRef={openRef}
      onDrop={onDrop}
      onReject={onReject ?? (() => {})}
      radius="md"
      pb={rem(30)}
      maxFiles={maxFiles ?? 1}
      multiple={multiple ?? false}
      maxSize={maxSize ?? 1024 ** 2}
      accept={mimeTypes}
      data-testid="dropzone"
      className="border-1 border-dashed border-gray-300 pt-5"
    >
      <div style={{ pointerEvents: "none" }}>
        <Group justify="center">
          <MantineDropzone.Accept>
            <IconDownload
              style={{ width: rem(50), height: rem(50) }}
              color={theme.colors.blue[6]}
              stroke={1.5}
            />
          </MantineDropzone.Accept>
          <MantineDropzone.Reject>
            <IconX
              style={{ width: rem(50), height: rem(50) }}
              color={theme.colors.red[6]}
              stroke={1.5}
            />
          </MantineDropzone.Reject>
          <MantineDropzone.Idle>
            <IconCloudUpload
              style={{ width: rem(50), height: rem(50) }}
              stroke={1.5}
            />
          </MantineDropzone.Idle>
        </Group>

        <Text ta="center" fw={700} fz="lg" mt="xl">
          <MantineDropzone.Accept>ここにファイルをドロップしてください</MantineDropzone.Accept>
          <MantineDropzone.Reject>このファイル形式はサポートされていません。</MantineDropzone.Reject>
          <MantineDropzone.Idle>{title}</MantineDropzone.Idle>
        </Text>
        <Text ta="center" fz="sm" mt="xs" c="dimmed">
          ここにファイルをドラッグ＆ドロップするか、クリックしてファイルを選択してください。<br />
          {acceptableMimeTypes.map((type, index) => (
            <React.Fragment key={type}>
              <i>{type}</i>
              {index < acceptableMimeTypes.length - 1 ? ", " : ""}
            </React.Fragment>
          ))} ファイルのみアップロード可能です。
        </Text>
      </div>
    </MantineDropzone>
  );
};

export default Dropzone;
